import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { resolveConfiguratorFlow, resolveConfiguratorFlowBase } from '@/utils/configurator-flow';
import { sessionCancel, sessionLoad, selectStaleUnpaidSession } from '@/store/slices/session';
import { blogClearNotices } from '@/store/slices/blog';
import { selectNShowcase } from '@/store/slices/userPreferences';

import { PageShell } from '../../components';
import { ConfirmDialog } from './components/ConfirmDialog';
import { BuyCreditsSheet } from '../creatives/components/BuyCreditsSheet';
import { setNewArticles } from '../blog/newArticles';
import { settleAutopilotTopup } from '../blog/autopilotTopup';
import { ROUTE } from '../../routes';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { useShowInProgress } from '../../hooks/useShowInProgress';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { useWebsiteVisitors } from '../website-analytics/useWebsiteVisitors';
import { useWebsiteTrafficQualityGate } from '../website-analytics/quality/gate';
import { writeFilters } from '../website-analytics/filters';
import { buildHomeMetrics, HOME_BRAND_VARIANTS, type HomeBrandVariant } from '../../mock-data/home';
import { acquisitionScoreForVariant, ACQ_BRAND_VARIANTS, type AcqBrandVariant } from '../../mock-data/acquisition';
import { useAcquisitionBudgetOverride } from '../acquisition/useAcquisitionBudget';
import { useAffiliateProgress } from '../acquisition/useAffiliateProgress';
import { useProductScore } from '../../hooks/useProductScore';
import { useFtuxSpotlight } from '../../features/ftux/spotlight/useFtuxSpotlight';
import { usePublishDeployStrip } from '../../features/ftux/shell/deployStripShown';
import type { HomePeriod } from '../../mock-data/types';
import {
  ImportantNow,
  useImportantNotices,
  usePendingUpgradeNotices,
  useBlogNotices,
  BrandDeploymentStrip,
  OptimizationScore,
  SectionHead,
  IntervalDropdown,
  AcquisitionMetric,
  FinancialPerformance,
  MoneyFlow,
  AcquisitionChannels,
  TopMarketingAssetsSection,
} from './components';
import {
  brandDeploymentSteps,
  isBrandDeploying,
  useCurrentBrand,
  useWatchForDeployStart,
} from './components/BrandDeploymentStrip/realDeployment';
import { websiteVisitorsFigure } from './components/AcquisitionMetric/AcquisitionMetric';
import { useHomeStats } from './useHomeStats';
import { useHomeChannels } from './useHomeChannels';
import { customDays, isHomeInterval } from './stats-mapping';
import { websitePeriodFor } from './shared-period';
import { usePeriodPicker } from './usePeriodPicker';
import { useStudioPeriod } from './useStudioPeriod';
import { HomePage, TwoCol, SpacedCard, PickerRow, SectionDivider } from './home.styles';

export type HomeProps = {
  styleText?: string;
  className?: string;
};

/** localStorage keys for the Home deploy-strip latches, persisted per-brand so the "Completed"
 *  celebration survives leaving Home / a reload and only ever clears on an explicit Close. */
const DEPLOY_ENGAGED_KEY = 'nova.home.deployEngagedBrandId';
const DEPLOY_DISMISSED_KEY = 'nova.home.deployDismissedBrandId';

const readDeployLatch = (key: string): string | undefined => {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};
const writeDeployLatch = (key: string, value: string | undefined): void => {
  try {
    if (value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* localStorage unavailable; the in-memory state still holds for this session */
  }
};

/**
 * Maps each "Acquisition Channels" card to its destination. The Lumio-managed
 * networks (web / apps / crypto / short videos) drill into the shared channel
 * detail template under their `ChannelId`; the self-managed channels go to their
 * own pages (Influencers is a placeholder until its module lands).
 */
const CHANNEL_ROUTE: Record<string, string> = {
  web: ROUTE.channelDetail('web-networks'),
  apps: ROUTE.channelDetail('app-networks'),
  crypto: ROUTE.channelDetail('crypto-sites'),
  short: ROUTE.channelDetail('short-videos'),
  affiliates: ROUTE.affiliates(),
  influencers: ROUTE.influencers(),
};

/**
 * Home dashboard ("Business Optimization Score") — the account landing page.
 * A pixel-faithful rebuild of the source dashboard's home view: the three
 * optimization-score gauges, then the per-interval Key Metrics (Acquisition,
 * Financial Performance, Money Flow) and the Acquisition Channels grid.
 *
 * Mounted as a fixed route at `/account/home`, shadowing the config-driven
 * account-home page. It holds the interval selection and the active brand.
 *
 * Data sourcing is MIXED:
 *  - Acquisition / Financial Performance / Money Flow are wired to the Owners Hub
 *    Stats API via {@link useHomeStats} (brand + interval drive the request; see
 *    `stats-mapping.ts`). Changing the interval re-requests for the new range.
 *  - The optimization-score gauges and the Acquisition Channels grid are still
 *    MOCK (`buildHomeMetrics`) — deliberately not wired yet.
 *
 * Brand switching: the active brand comes from the `casinoInstance` slice (set by
 * the top-bar `BrandSelector`). The transition is global — `BrandTransition`
 * (mounted around every route) fades the whole view out, shows the logo loader +
 * skeleton while the brand-switch middleware promotes the lagged `displayBrand`
 * under cover, then fades the new view in. The page reads `displayBrand` for both
 * the (still-mock) gauges/channels dataset and the stats brand id, so its content
 * tracks the lagged brand through the fade.
 */
const Home = ({ styleText = '', className = '' }: HomeProps): React.ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // The range the owner last picked on any page with one (this page's default until then).
  const [activeInterval, setActiveInterval] = useStudioPeriod('7D');

  const brands = useAppSelector((state) => state.casinoInstance.brands, isEqual);
  // Read `displayBrand` (the lagged content brand), not `activeBrand`: it only
  // promotes to the picked brand once the view is hidden behind the loader, so the
  // previous brand's data stays on screen through the fade-out. The switcher reads
  // `activeBrand` and updates immediately.
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand, isEqual);

  // Map the displayed brand → one of the five mock datasets by its list position,
  // cycling. Same brand always renders the same dataset; an unknown/absent brand
  // falls back to the canonical dataset (0). Replace with the brand id + a real
  // per-brand selector when the metrics API exists.
  const variant = React.useMemo<HomeBrandVariant>(() => {
    if (!displayBrand) return 0;
    const idx = brands.findIndex((brand) => brand.id === displayBrand.id);
    return (Math.max(0, idx) % HOME_BRAND_VARIANTS.length) as HomeBrandVariant;
  }, [brands, displayBrand]);

  // The Acquisition optimization-score gauge must show the SAME score the
  // Acquisition page computes for this brand (from its campaign queue + budget),
  // not the standalone mock figure. It is brand-scoped by the acquisition variant
  // mapping (own modulo), so resolve that index and pull the score/label from the
  // single source of truth.
  const acqVariant = React.useMemo<AcqBrandVariant>(() => {
    if (!displayBrand) return 0;
    const idx = brands.findIndex((brand) => brand.id === displayBrand.id);
    return (Math.max(0, idx) % ACQ_BRAND_VARIANTS.length) as AcqBrandVariant;
  }, [brands, displayBrand]);

  // Feed the SAME real acquisition budget the Acquisition page uses into this brand's
  // Acquisition gauge, so Home and the Acquisition page never show different scores.
  const { override: acqBudgetOverride } = useAcquisitionBudgetOverride(displayBrand?.id);

  // ...and the SAME suggested-campaign count the Acquisition page feeds into the
  // score's campaign half, so the two gauges agree on the queue as well as the budget.
  // It is 0 on both pages: the Ad Service reports created campaigns, not suggestions,
  // and the mapper surfaces only the live ones, so the queue is genuinely empty until
  // a real suggestions source (Lumio) lands. `pages/acquisition/index.tsx` must keep
  // this value identical; no pool fetch is needed here while it is a constant.
  const acqSuggestedCount = 0;

  // Sections backed by data we can't derive yet (Player Balance) hide behind the
  // `show_in_progress` localStorage flag rather than shipping a bare `?`. Outside that
  // plain "777" reveal the Acquisition page also hides its Lumio sections and scores the
  // affiliate program instead, so this gauge does too, from the same reads.
  const showInProgress = useShowInProgress();
  const affiliateProgress = useAffiliateProgress(showInProgress ? undefined : displayBrand?.id, true);
  const affiliateAcqScore = affiliateProgress.score.score;
  const affiliateAcqStatus = affiliateProgress.score.status;

  // The Product optimization-score gauge must show the SAME website-competitiveness score the
  // Product page computes for this brand (from its enabled features), not a standalone figure.
  // `useProductScore` is that single source of truth (and owns the catalog fetch).
  const productScore = useProductScore();

  const data = React.useMemo(() => {
    // The mock gauges know only the presets; a custom range reads like the last 30 days there.
    const base = buildHomeMetrics(isHomeInterval(activeInterval) ? activeInterval : '30D', variant);
    const acq = showInProgress
      ? acquisitionScoreForVariant(acqVariant, acqBudgetOverride, acqSuggestedCount)
      : { score: affiliateAcqScore, status: affiliateAcqStatus };
    // Override the Acquisition and Product gauges so each mirrors its area page's
    // headline score (the Acquisition page's computed score; the Product page's
    // website-competitiveness gauge) rather than the standalone mock figure that
    // drifts by interval/brand. The Operations gauge keeps its own mock figure
    // until that page exposes a score of its own.
    const gauges = base.gauges.map((gauge) => {
      if (gauge.id === 'acq') return { ...gauge, value: acq.score, status: acq.status };
      if (gauge.id === 'product') return { ...gauge, value: productScore.score, status: productScore.status };
      return gauge;
    });
    return { ...base, gauges };
  }, [
    activeInterval,
    variant,
    acqVariant,
    acqBudgetOverride,
    acqSuggestedCount,
    showInProgress,
    affiliateAcqScore,
    affiliateAcqStatus,
    productScore.score,
    productScore.status,
  ]);

  // Key Metrics + Acquisition Channels come from the Stats API, keyed on the
  // displayed brand + the selected interval. Changing the interval re-requests for
  // the new range; the delta caption adapts to it too.
  const stats = useHomeStats(displayBrand?.id, activeInterval);
  const channelStats = useHomeChannels(displayBrand?.id, activeInterval);
  // "Top marketing assets" links into Marketing Assets, so it shows only while that section is available (its routes
  // answer Not Found otherwise).
  const { isEnabled: isFeatureEnabled } = useFeatureGate();
  const showTopAssets = isFeatureEnabled('creatives-assets');
  // The brand's website visitors beside the Acquisition sign-ups, while Website analytics is shown (its flag) and the
  // analytics service answers; the figure is left out otherwise.
  const websiteStatsOn = isFeatureEnabled('website-analytics');
  const tenantRaw = Number(displayBrand?.tenant_id);
  const visitorsTenant = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const trafficQuality = useWebsiteTrafficQualityGate(visitorsTenant).quality;
  const websiteVisitors = useWebsiteVisitors(visitorsTenant, activeInterval, websiteStatsOn, trafficQuality);
  // Each caption is a literal key, so the extractor harvests it.
  const visitorsCaption = ((): string | undefined => {
    switch (websiteVisitors.caption) {
      case 'Internal addresses only':
        return t('Internal addresses only');
      case 'No public address yet':
        return t('No public address yet');
      case 'Not available right now':
        return t('Not available right now');
      default:
        return undefined;
    }
  })();

  // `COMPARISON_LABEL` is a module-scope English map (also
  // the i18n keys); translate its displayed copy here at the render site so it
  // re-evaluates on a language switch (never `t()` at module scope). Each branch
  // wraps an explicit literal so the extractor harvests every string.
  const comparison = React.useMemo<string>(() => {
    switch (activeInterval) {
      case 'TODAY':
        return t('vs yesterday');
      case 'YESTERDAY':
        return t('vs previous day');
      case '7D':
        return t('vs previous 7 days');
      case '30D':
        return t('vs previous 30 days');
      case '90D':
        return t('vs previous 90 days');
      case '6M':
        return t('vs previous 6 months');
      case '1Y':
        return t('vs previous year');
      default:
        // A custom range compares with the same number of days just before it.
        return t('vs the {{n}} days before', { n: customDays(activeInterval)?.days ?? 7 });
    }
  }, [activeInterval, t]);

  // The range picker: the presets plus a custom run of days (shared with Stats).
  const periodPicker = usePeriodPicker(activeInterval, setActiveInterval);

  // Prefix the Key Metrics heading with the active brand's name (falls back to a
  // plain "Key Metrics" while a brand is still mid-setup / unnamed).
  const brandName = displayBrand?.casino_data?.details?.brand ?? '';

  // The optimization-score gauges are still MOCK: they load on a randomised delay.
  const scores = useMockEndpoint(data.gauges);
  // The Newton showcase shows no scores, so the gauges section is left out.
  const newtonShowcase = useAppSelector(selectNShowcase);

  // "Important Now" notifications (payment + new-casino setup) — derived from the
  // session + brand list; the banner hides itself when there's nothing pressing. Order: Lumio's pick
  // leads while the FTUX has a piece waiting (none for an owner outside the FTUX), then "Continue
  // building your website" (an unpaid site left behind: always second, right after the pick; the
  // FTUX runs beside it), then any pending feature-upgrade invoices as premium "Finish your upgrade"
  // cards (money not yet spent), then the other session-derived notices, then the blog's Autopilot
  // notices (credits it is waiting for, articles it published on its own).
  const ftuxNotices = useFtuxSpotlight();
  const importantNotices = useImportantNotices();
  const pendingUpgradeNotices = usePendingUpgradeNotices();
  const blogNotices = useBlogNotices();
  const notices = React.useMemo(() => {
    const unpaidSite = importantNotices.filter((notice) => notice.kind === 'configurator-unpaid');
    const sessionNotices = importantNotices.filter((notice) => notice.kind !== 'configurator-unpaid');
    return [...ftuxNotices, ...unpaidSite, ...pendingUpgradeNotices, ...sessionNotices, ...blogNotices];
  }, [ftuxNotices, pendingUpgradeNotices, importantNotices, blogNotices]);
  const dispatch = useAppDispatch();

  // Autopilot cards (`docs/auto-generation.md` in spark-blog, "Home Spotlight"). "See articles"
  // marks the ids for the blog list's "New" badges (sessionStorage, per brand), closes the notice
  // and opens the list; the clear call is not awaited, the list is where the owner is going and the
  // slice installs the answer whenever it lands. "Clear" alone closes the notice. "Top up" opens the
  // buy-credits sheet primed with the price the refused run needed; once the credits land the
  // notice is closed, the missed article is asked for soon (`resume`), the wallet chip and the
  // notices are re-read. The sheet is mounted here, as the editor and the creatives pages mount
  // theirs, so the flow needs nothing from the layout.
  const onSeeNewArticles = React.useCallback(
    (brandId: string, articleIds: string[]) => {
      setNewArticles(brandId, articleIds);
      void dispatch(blogClearNotices({ articles: true }));
      navigate(ROUTE.blog());
    },
    [dispatch, navigate],
  );
  const onClearNewArticles = React.useCallback(() => {
    void dispatch(blogClearNotices({ articles: true }));
  }, [dispatch]);
  const [topupOpen, setTopupOpen] = React.useState(false);
  const [topupSuggested, setTopupSuggested] = React.useState<number | undefined>(undefined);
  const onTopUpAutopilot = React.useCallback((_brandId: string, creditsNeeded: number) => {
    setTopupSuggested(creditsNeeded > 0 ? creditsNeeded : undefined);
    setTopupOpen(true);
  }, []);
  const onClearAutopilotCredits = React.useCallback(() => {
    void dispatch(blogClearNotices({ credits: true }));
  }, [dispatch]);
  const onTopupSettled = React.useCallback(() => {
    void settleAutopilotTopup(dispatch, { walletFresh: true });
  }, [dispatch]);
  const onTopupClose = React.useCallback(() => setTopupOpen(false), []);
  // The owner's half-finished, unpaid configurator session (if any) — drives the
  // "Continue building your website" banner card + its cancel confirmation.
  const staleSession = useAppSelector(selectStaleUnpaidSession, isEqual);
  const cancelling = useAppSelector((s) => s.session.stale.cancelling);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);

  // The "Deploying … / Completed" deploy strip is scoped to the SELECTED brand: engage once we
  // observe that brand deploying (the latch persists into the "Completed" state), and remember the
  // dismiss per brand. So switching brands re-scopes cleanly — an already-live brand never shows
  // it, and closing one brand's celebration doesn't hide another brand's deploy.
  //
  // BOTH latches are PERSISTED (localStorage), NOT ephemeral component state: the finished strip
  // must NOT auto-dismiss. It stays on Home across navigation away-and-back and reloads, and only
  // ever disappears when the owner explicitly presses Close.
  const deployBrand = useCurrentBrand();
  const deployBrandId = deployBrand?.id;
  const deployingBrand = isBrandDeploying(deployBrand);
  // A finished brand is worth surfacing only while it still carries a pipeline (guards against a
  // persisted latch resurrecting an empty strip if the backend later drops the steps).
  const hasDeploySteps = React.useMemo(() => brandDeploymentSteps(deployBrand).length > 0, [deployBrand]);
  const [engagedBrandId, setEngagedBrandId] = React.useState<string | undefined>(() =>
    readDeployLatch(DEPLOY_ENGAGED_KEY),
  );
  const [dismissedBrandId, setDismissedBrandId] = React.useState<string | undefined>(() =>
    readDeployLatch(DEPLOY_DISMISSED_KEY),
  );
  React.useEffect(() => {
    if (!deployingBrand || deployBrandId === undefined) return;
    // Observed this brand deploying: latch it (persisted), and clear any stale dismissal so a NEW
    // deploy for the same brand resurfaces the strip (a dismissal only suppresses the finished one).
    setEngagedBrandId(deployBrandId);
    writeDeployLatch(DEPLOY_ENGAGED_KEY, deployBrandId);
    setDismissedBrandId((prev) => {
      if (prev !== deployBrandId) return prev;
      writeDeployLatch(DEPLOY_DISMISSED_KEY, undefined);
      return undefined;
    });
  }, [deployingBrand, deployBrandId]);
  const onDismissDeployment = React.useCallback(() => {
    setDismissedBrandId(deployBrandId);
    writeDeployLatch(DEPLOY_DISMISSED_KEY, deployBrandId);
  }, [deployBrandId]);
  // A brand that is ACTIVELY deploying ALWAYS shows — the live state can never be suppressed by a
  // stale latch. The persisted engaged/dismissed latches govern ONLY the FINISHED celebration: it
  // lingers (across navigation / reload) until the owner presses Close, then stays gone.
  const showDeployment =
    deployBrandId !== undefined &&
    (deployingBrand || (engagedBrandId === deployBrandId && dismissedBrandId !== deployBrandId && hasDeploySteps));
  // FTUX - Getting Started: its pieces wait while the strip shows (the finished summary until Close).
  usePublishDeployStrip(showDeployment);
  // While nothing deploy-related is on screen, briefly poll the brand list so a deploy triggered
  // right before landing on Home surfaces within seconds (the strip's own 7s poll can't run until
  // it is already mounted). Stops the moment the strip shows, and self-limits for idle owners.
  useWatchForDeployStart(showDeployment);

  const onRetryPayment = React.useCallback(() => navigate(resolveConfiguratorFlow().paymentPath), [navigate]);
  const onContinueSetup = React.useCallback(() => navigate(ROUTE.customizer()), [navigate]);

  // Resume the stale session: load it into the active configurator session FIRST
  // (so the wizard hydrates from it instead of minting / resuming a different one),
  // then enter the configurator.
  const onContinueUnpaid = React.useCallback(async () => {
    if (!staleSession) return;
    try {
      await dispatch(sessionLoad({ id: staleSession.id })).unwrap();
    } catch {
      // Even if the load fails, fall through — the configurator will retry the load
      // for the persisted id on mount rather than stranding the owner here.
    }
    navigate(resolveConfiguratorFlowBase());
  }, [dispatch, navigate, staleSession]);

  const onCancelUnpaid = React.useCallback(() => setCancelConfirmOpen(true), []);
  const onDismissCancel = React.useCallback(() => setCancelConfirmOpen(false), []);
  const onConfirmCancel = React.useCallback(async () => {
    if (!staleSession) {
      setCancelConfirmOpen(false);
      return;
    }
    try {
      await dispatch(sessionCancel({ session_id: staleSession.id })).unwrap();
    } catch {
      // Surfaced via the slice's stale.error; keep the dialog open so the owner can retry.
      return;
    }
    setCancelConfirmOpen(false);
  }, [dispatch, staleSession]);

  // Open Website Settings and deep-link to the deployment-status table; the section
  // reads the `#deployment` hash and smooth-scrolls itself into view on arrival.
  const onViewDeployment = React.useCallback(
    () => navigate(`${ROUTE.settingsSection('website-settings')}#deployment`),
    [navigate],
  );

  // Finish paying a pending feature upgrade: open the feature's product-detail page, where the
  // "Finish payment" flow lives (the premium Spotlight card is the entry point).
  const onFinishFeaturePayment = React.useCallback(
    (itemId: number) => navigate(ROUTE.productDetail(String(itemId))),
    [navigate],
  );

  const onOpenProduct = React.useCallback(() => navigate(ROUTE.product()), [navigate]);
  const onOpenAcquisition = React.useCallback(() => navigate(ROUTE.acquisition()), [navigate]);
  const onOpenOperations = React.useCallback(() => navigate(ROUTE.operations()), [navigate]);
  const onOpenSignups = React.useCallback(() => navigate(ROUTE.stats()), [navigate]);
  const onOpenFinancial = React.useCallback(() => navigate(ROUTE.statsFinancial()), [navigate]);
  const onOpenMoneyFlow = React.useCallback(() => navigate(ROUTE.statsMoneyFlow()), [navigate]);
  // The visitors figure opens Stats → Website on the same period, as closely as that tab can show it (see
  // `websitePeriodFor`: 6M and 1Y open its last 90 days, a custom run past 90 days its own last 90 days).
  const onOpenVisitors = React.useCallback(() => {
    const search = writeFilters(new URLSearchParams(), { interval: websitePeriodFor(activeInterval) }).toString();
    navigate(search ? `${ROUTE.statsWebsite()}?${search}` : ROUTE.statsWebsite());
  }, [navigate, activeInterval]);

  const onOpenChannel = React.useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      const to = id ? CHANNEL_ROUTE[id] : undefined;
      if (to) navigate(to);
    },
    [navigate],
  );

  return (
    <PageShell styleText={styleText} className={className}>
      <HomePage>
        <ImportantNow
          notices={notices}
          onRetryPayment={onRetryPayment}
          onContinueSetup={onContinueSetup}
          onContinueUnpaid={onContinueUnpaid}
          onCancelUnpaid={onCancelUnpaid}
          onViewDeployment={onViewDeployment}
          onFinishFeaturePayment={onFinishFeaturePayment}
          onSeeNewArticles={onSeeNewArticles}
          onClearNewArticles={onClearNewArticles}
          onTopUpAutopilot={onTopUpAutopilot}
          onClearAutopilotCredits={onClearAutopilotCredits}
        />

        {topupOpen ? (
          <BuyCreditsSheet suggestedCredits={topupSuggested} onClose={onTopupClose} onSuccess={onTopupSettled} />
        ) : null}

        <ConfirmDialog
          open={cancelConfirmOpen}
          title={t('Discard this website?')}
          message={t("This permanently deletes the website you started building. This can't be undone.")}
          note={t('You can start a new brand anytime from the brand switcher at the top of the page.')}
          confirmLabel={t('Discard website')}
          cancelLabel={t('Keep building')}
          loading={cancelling}
          onConfirm={onConfirmCancel}
          onCancel={onDismissCancel}
        />

        {showDeployment ? (
          <>
            <BrandDeploymentStrip onDismiss={onDismissDeployment} />
            <SectionDivider role="separator" />
          </>
        ) : null}

        {newtonShowcase ? null : (
          <OptimizationScore
            gauges={scores.data}
            loading={scores.loading || (!showInProgress && affiliateProgress.scoreLoading)}
            onOpenProduct={onOpenProduct}
            onOpenAcquisition={onOpenAcquisition}
            onOpenOperations={onOpenOperations}
          />
        )}

        <SectionHead
          title={brandName ? t('{{brand}} Key Metrics') : t('Key Metrics')}
          placeholders={brandName ? { brand: brandName } : undefined}
          sticky
          right={
            <PickerRow>
              <IntervalDropdown
                value={activeInterval}
                options={periodPicker.options}
                onChange={setActiveInterval}
                custom={periodPicker.custom}
              />
            </PickerRow>
          }
        />

        <AcquisitionMetric
          data={stats.acquisition.data}
          comparison={comparison}
          interval={activeInterval}
          loading={stats.acquisition.loading}
          visitors={
            websiteStatsOn
              ? websiteVisitorsFigure(websiteVisitors, {
                  people: trafficQuality,
                  caption: visitorsCaption,
                  bucketStarts: stats.acquisition.data.bucketStarts,
                })
              : undefined
          }
          onOpenVisitors={onOpenVisitors}
          onOpenSignups={onOpenSignups}
        />

        <TwoCol>
          <FinancialPerformance
            data={stats.financial.data}
            comparison={comparison}
            interval={activeInterval}
            loading={stats.financial.loading}
            onOpen={onOpenFinancial}
          />
          <MoneyFlow
            data={stats.moneyFlow.data}
            comparison={comparison}
            loading={stats.moneyFlow.loading}
            showInProgress={showInProgress}
            onOpen={onOpenMoneyFlow}
          />
        </TwoCol>

        {showTopAssets ? (
          <SpacedCard>
            <TopMarketingAssetsSection casinoInstanceId={displayBrand?.id} interval={activeInterval} />
          </SpacedCard>
        ) : null}

        <SpacedCard>
          <AcquisitionChannels
            channels={channelStats.channels}
            dates={channelStats.dates}
            pointLabels={channelStats.pointLabels}
            interval={activeInterval}
            comparison={comparison}
            onOpenChannel={onOpenChannel}
          />
        </SpacedCard>
      </HomePage>
    </PageShell>
  );
};

export default Home;
